import { KioskDto, KnownKioskDtoRole, KnownKioskDtoStatus } from '@groundfloor-platform/mgmt-service-g-sdk';
import { KioskConnectivityStatusUpdate, KioskDtoConnectionStatus } from '@groundfloor-platform/types-event-grid-events';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';
import { useImmer } from 'use-immer';
import {
  KioskConnectivitySubscriptionCallback,
  KioskConnectivitySubscriptionMatcher,
  KioskConnectivitySubscriptionStream
} from '../../events/kioskConnectivity/kioskConnectivity.types';
import { useKioskConnectivityEventCentre } from '../../events/kioskConnectivity/useKioskConnectivityEventCentre.hook';
import { useQueryKiosksList } from './kiosk.queries';

enum KioskConnectivityStatusUpdateType {
  Green = 'green',
  Orange = 'orange',
  Red = 'red',
  Grey = 'grey'
}

export type KioskConnectivityStatusUpdates = {
  green: InternalKioskConnectivityUpdate[];
  orange: InternalKioskConnectivityUpdate[];
  red: InternalKioskConnectivityUpdate[];
  grey: InternalKioskConnectivityUpdate[];
};

interface State {
  connectionID?: string | null;
  isLoading: boolean;
  kioskIDs: string[];
  updates: KioskConnectivityStatusUpdates;
  untouchedKioskIDs: string[];
}

const initialState: State = {
  isLoading: false,
  kioskIDs: [],
  updates: {
    green: [],
    orange: [],
    red: [],
    grey: []
  },
  untouchedKioskIDs: []
};

export interface UseKioskConnectivityStatusHookResult {
  kiosks: KioskDto[];
  updates: KioskConnectivityStatusUpdates;
  untouchedKioskIDs: string[];
  deactivateAlert(kioskIDS: string[]): void;
}

export interface InternalKioskConnectivityUpdate extends KioskConnectivityStatusUpdate {
  status?: KnownKioskDtoStatus;
}

export function useKioskConnectivityStatusBar(): UseKioskConnectivityStatusHookResult {
  const [state, setState] = useImmer(initialState);

  const kiosks = useQueryKiosksList();
  const filteredKiosks = useMemo(() => {
    return (kiosks.data || []).filter((k) => k.role === KnownKioskDtoRole.Manager && !!k.iotHubID);
  }, [kiosks.data]);
  const iotHubIDsInScope = useMemo(() => filteredKiosks.map((k) => k.iotHubID as string), [filteredKiosks]);
  const getKioskDto = useCallback(
    (iotHubID: string) => {
      return filteredKiosks?.find((k) => k.iotHubID === iotHubID);
    },
    [filteredKiosks]
  );
  // subscribe to kiosk connectivity events
  const callback: KioskConnectivitySubscriptionCallback = useCallback(
    (event) => {
      const kiosk = getKioskDto(event.data.iotHubID);
      const internalUpdate: InternalKioskConnectivityUpdate = {
        ...event.data,
        status: kiosk?.status as KnownKioskDtoStatus
      };
      setState((draft) => {
        // Make assertions before updating state
        const shouldShowAlert = assertShouldShowAlert(draft, internalUpdate);
        const isNewUpdate = assertIsNewUpdate(draft, internalUpdate);
        // Update state
        removeKioskFromUpdates(draft.updates, internalUpdate.iotHubID);
        addKioskToUpdates(draft.updates, internalUpdate);
        if (shouldShowAlert && isNewUpdate) {
          draft.untouchedKioskIDs.push(internalUpdate.iotHubID);
        }
      });
    },
    [setState, getKioskDto]
  );

  const matcher: KioskConnectivitySubscriptionMatcher = useCallback(
    (event) => {
      return iotHubIDsInScope.includes(event.data.iotHubID);
    },
    [iotHubIDsInScope]
  );

  const stream: KioskConnectivitySubscriptionStream = useMemo(
    () => ({
      iotHubIDs: iotHubIDsInScope
    }),
    [iotHubIDsInScope]
  );

  useKioskConnectivityEventCentre({ callback, matcher, stream });

  // This useMemo maintains the "initial value" of the kiosk status updates
  // The initial value represents the status of the kiosks loaded form the database
  // When the query re-fetches, we reset the initial states
  const initialKioskStatuses: InternalKioskConnectivityUpdate[] = useMemo(
    () =>
      (filteredKiosks || []).map((k) => ({
        iotHubID: k.iotHubID as string,
        connectionStatus: k.connectionStatus as KioskDtoConnectionStatus,
        lastConnectedTime: k.lastConnectedTime?.toISOString(),
        status: k.status as KnownKioskDtoStatus
      })),
    [filteredKiosks]
  );

  // This useEffect sets the initial state of the updates to state whenever they change (i.e. when the user
  // switches project) therefore overwriting any irrelevant updates that have previously come form SignalR
  useEffect(() => {
    setState((draft) => {
      draft.updates.green = [];
      draft.updates.orange = [];
      draft.updates.red = [];
      draft.updates.grey = [];
      draft.untouchedKioskIDs = [];
      for (const state of initialKioskStatuses) {
        addKioskToUpdates(draft.updates, state);
      }
    });
  }, [initialKioskStatuses, setState]);

  // This useEffect causes the data to be updated every minute. This achieves 2 things:
  //    1. Keeps the values for time offline up to date
  //    2. Will move updates between red and orange as their time offline passes the limit
  useEffect(() => {
    const interval = setInterval(() => {
      setState((draft) => {
        const offlineUpdates = [...draft.updates.orange, ...draft.updates.red];
        draft.updates.orange = [];
        draft.updates.red = [];

        for (const update of offlineUpdates) {
          addKioskToUpdates(draft.updates, update);
          if (assertIsNewRedUpdate(draft, update)) {
            draft.untouchedKioskIDs.push(update.iotHubID);
          }
        }
      });
    }, 1000 * 60);
    return () => {
      clearInterval(interval);
    };
  }, [setState]);

  // Callback passed down to components displaying alerts allowing the component to cancel the alert
  // This is useful, for example, for hiding the alert once a user has hovered over it
  const deactivateAlert = (kioskIDs: string[]) => {
    setState((draft) => {
      draft.untouchedKioskIDs = draft.untouchedKioskIDs.filter((id) => !kioskIDs.includes(id));
    });
  };
  return {
    kiosks: filteredKiosks,
    updates: state.updates,
    untouchedKioskIDs: state.untouchedKioskIDs,
    deactivateAlert
  } as const;
}

function getKioskConnectivityStatusUpdateType(
  update: InternalKioskConnectivityUpdate
): KioskConnectivityStatusUpdateType {
  if (update.status === KnownKioskDtoStatus.Inactive) {
    return KioskConnectivityStatusUpdateType.Grey;
  }
  if (update.connectionStatus === KioskDtoConnectionStatus.Online) {
    return KioskConnectivityStatusUpdateType.Green;
  }
  const tenMins = 1000 * 60 * 10;
  if (
    update.lastConnectedTime !== undefined &&
    dayjs(update.lastConnectedTime).isValid() &&
    Math.abs(dayjs(update.lastConnectedTime).diff(new Date())) < tenMins
  ) {
    return KioskConnectivityStatusUpdateType.Orange;
  }
  return KioskConnectivityStatusUpdateType.Red;
}

function assertShouldShowAlert(state: State, update: InternalKioskConnectivityUpdate) {
  const currentStatusType = Object.entries(state.updates).find(([, value]) => {
    return value.find((u) => u.iotHubID === update.iotHubID) !== undefined;
  })?.[0];
  const updateType = getKioskConnectivityStatusUpdateType(update);
  return (
    (!currentStatusType && updateType === 'green') ||
    (currentStatusType === 'red' && updateType === 'green') ||
    updateType === 'red'
  );
}

function assertIsNewUpdate(state: State, update: InternalKioskConnectivityUpdate) {
  const updateType = getKioskConnectivityStatusUpdateType(update);
  return state.updates[updateType].filter((u) => u.iotHubID === update.iotHubID).length === 0;
}

function assertIsNewRedUpdate(state: State, update: InternalKioskConnectivityUpdate) {
  const prevRedKioskIDs = state.updates.red.map((u) => u.iotHubID);
  const updateType = getKioskConnectivityStatusUpdateType(update);
  return updateType === KioskConnectivityStatusUpdateType.Red && !prevRedKioskIDs.includes(update.iotHubID);
}

function removeKioskFromUpdates(updates: KioskConnectivityStatusUpdates, iotHubID: string) {
  updates.green = updates.green.filter((u) => u.iotHubID !== iotHubID);
  updates.orange = updates.orange.filter((u) => u.iotHubID !== iotHubID);
  updates.red = updates.red.filter((u) => u.iotHubID !== iotHubID);
  updates.grey = updates.grey.filter((u) => u.iotHubID !== iotHubID);
}

function addKioskToUpdates(updates: KioskConnectivityStatusUpdates, update: InternalKioskConnectivityUpdate) {
  const updateType = getKioskConnectivityStatusUpdateType(update);
  updates[updateType].push(update);
}
